/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, styled, Tab, Tabs as MuiTabs } from "@mui/material";
import * as React from "react";

import Analytics from "../services/analytics";

const CustomBox = styled(Box)`
  position: relative;
  height: 48px;
  overflow: visible;
  ::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    bottom: 0px;
    border: 1px solid ${({ theme }) => theme.colors.primary300};
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    height: 38px;
  }
`;

const StyledTab = styled(Tab)`
  /* padding-top: 0px; */
  & .MuiTouchRipple-root {
    ::after {
      content: "";
      position: relative;
      left: 0;
      right: 0;
      top: -12px;
      bottom: -2px;
      z-index: 1; // Add this line
    }
  }
`;

const StyledMuiTabs = styled(MuiTabs)<{ gap?: string | number }>(({ theme, gap }) => ({
  top: "-9px",
  position: "absolute",
  [theme.breakpoints.down("sm")]: {
    top: "-10px",
  },
  "& .MuiTabs-flexContainer": {
    gap: gap ?? "80px",
    [theme.breakpoints.down("sm")]: {
      gap: "24px",
    },
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  id?: string;
}

export const TabPanel = ({ children, value, index, id, ...rest }: TabPanelProps) => {
  // Generate unique ids for accessibility
  const tabPanelId = `${id}-tabpanel-${index}`;
  const tabId = `${id}-tab-${index}`;

  // Determine if the panel should be hidden
  const hidden = value !== index;

  return (
    <div role="tabpanel" hidden={hidden} id={tabPanelId} aria-labelledby={tabId} {...rest}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number, id: string | undefined) => {
  return {
    id: `${id}-tab-${index}`,
    "aria-controls": `${id}-tabpanel-${index}`,
  };
};

interface TabsProps {
  tabsInfo: {
    label: string;
    "data-testid"?: string;
    details: React.ReactNode;
    default: boolean;
  }[];
  ariaLabel?: string;
  id?: string;
}

const Tabs = ({
  tabsInfo,
  ariaLabel,
  value,
  setValue,
  id = "",
}: TabsProps & { value: number; setValue: React.Dispatch<React.SetStateAction<number>> }) => {
  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    Analytics.push({
      dataLayer: {
        event: "tab_agreements",
        tab_term: tabsInfo[newValue].label,
      },
    });
    setValue(newValue);
  };

  return (
    <Box>
      <CustomBox data-id="custom-bopx">
        <StyledMuiTabs value={value} onChange={handleChange} aria-label={ariaLabel}>
          {tabsInfo.map((x, index) => (
            <StyledTab
              data-testid={x["data-testid"]}
              key={x.label}
              label={x.label}
              {...a11yProps(index, id)}
              disableRipple
            />
          ))}
        </StyledMuiTabs>
      </CustomBox>
    </Box>
  );
};

export default Tabs;
